import React, { useMemo } from "react";
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine
} from "recharts";
import SleepChart from "./Sleepchart";

/* ─── Custom Tooltip ───────────────────────────────────────────── */
function VitalTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#1e293b", border: "1px solid #334155",
      borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#e2e8f0"
    }}>
      <div style={{ color: "#94a3b8", marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color }}>
          {p.name}: <strong>{typeof p.value === "number" ? p.value.toFixed(1) : p.value} {unit}</strong>
        </div>
      ))}
    </div>
  );
}

/* ─── Date range helpers ────────────────────────────────────────── */
function startOfDay(d)  { const x = new Date(d); x.setHours(0,0,0,0); return x; }
function startOfWeek(d) { const x = startOfDay(d); x.setDate(x.getDate() - x.getDay()); return x; }
function startOfMonth(d){ return new Date(d.getFullYear(), d.getMonth(), 1); }
function addDays(d, n)  { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function addWeeks(d, n) { return addDays(d, n * 7); }
function addMonths(d, n){ const x = new Date(d); x.setMonth(x.getMonth() + n); return x; }

export function filterAndAggregate(rawData, viewMode, anchor, config) {
  if (!rawData?.length) return [];

  const anchorDate = new Date(anchor);
  let rangeStart, rangeEnd;

  if (viewMode === "day") {
    rangeStart = startOfDay(anchorDate);
    rangeEnd   = addDays(rangeStart, 1);
  } else if (viewMode === "week") {
    rangeStart = startOfWeek(anchorDate);
    rangeEnd   = addWeeks(rangeStart, 1);
  } else {
    rangeStart = startOfMonth(anchorDate);
    rangeEnd   = addMonths(rangeStart, 1);
  }

  const rsMs = rangeStart.getTime();
  const reMs = rangeEnd.getTime();

  const inRange = rawData.filter(row => {
    const ts = row.ts || 0;
    return ts >= rsMs && ts < reMs;
  });

  if (viewMode === "day") {
    return inRange.map(row => ({
      ...row,
      label: row.ts
        ? new Date(row.ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
        : row.date,
    }));
  }

  const byDay = {};
  inRange.forEach(row => {
    const k = row.dateKey || row.date;
    if (!byDay[k]) byDay[k] = { rows: [], dateLabel: row.date };
    byDay[k].rows.push(row);
  });

  return Object.entries(byDay).map(([, { rows, dateLabel }]) => {
    if (config.key === "activity") {
      return {
        label:    dateLabel,
        steps:    rows.reduce((s, r) => s + (r.steps    || 0), 0),
        calories: rows.reduce((s, r) => s + (r.calories || 0), 0),
      };
    }
    const avg = +(rows.reduce((s, r) => s + (r[config.dataKey] || 0), 0) / rows.length).toFixed(1);
    return { label: dateLabel, [config.dataKey]: avg };
  });
}

/* ══════════════════════════════════════════════════════════════
   VitalChart
   Props:
     config         – vital config object from VITALS
     rawData        – processed trend data (ignored for sleep)
     rawSleepItems  – raw wearable items with type === "sleep"
     viewMode       – "day" | "week" | "month"
     anchor         – current Date
     setAnchor      – setter passed down to SleepChart calendar picker
══════════════════════════════════════════════════════════════ */
export default function VitalChart({ config, rawData, rawSleepItems, viewMode, anchor, setAnchor }) {
  const isSleep    = config.key === "sleep";
  const isActivity = config.key === "activity";

  const data = useMemo(
    () => isSleep ? [] : filterAndAggregate(rawData, viewMode, anchor, config),
    [rawData, viewMode, anchor, config, isSleep]
  );

  const isEmpty = isSleep
    ? !rawSleepItems?.length
    : !data || data.length === 0;

  /* Stats for non-sleep, non-activity vitals */
  const stats = useMemo(() => {
    if (!data?.length || isSleep || isActivity) return null;
    const vals = data.map(d => d[config.dataKey]).filter(v => v != null);
    if (!vals.length) return null;
    const avg    = (vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(1);
    const min    = Math.min(...vals).toFixed(1);
    const max    = Math.max(...vals).toFixed(1);
    const latest = vals[vals.length - 1].toFixed(1);
    return { avg, min, max, latest };
  }, [data, config.dataKey, isSleep, isActivity]);

  const tickInterval = viewMode === "month"
    ? Math.max(0, Math.floor(data.length / 8))
    : "preserveStartEnd";

  /* ── Sleep — delegate entirely to SleepChart ── */
  if (isSleep) {
    return (
      <div className="dv-vital-card">
        <div className="dv-vital-header">
          <span className="dv-vital-label">{config.label}</span>
        </div>
        <SleepChart
          rawSleepItems={rawSleepItems}
          viewMode={viewMode}
          anchor={anchor}
          setAnchor={setAnchor}
        />
      </div>
    );
  }

  /* ── All other vitals ── */
  return (
    <div className="dv-vital-card">
      <div className="dv-vital-header">
        <span className="dv-vital-label">{config.label}</span>
        {config.normal && (
          <span className="dv-vital-normal">Normal: {config.normal}</span>
        )}
      </div>

      {/* Stats row */}
      {stats && (
        <div className="dv-stats-row">
          <div className="dv-stat-pill">
            <span className="dv-stat-lbl">Latest</span>
            <strong style={{ color: config.color }}>{stats.latest} {config.unit}</strong>
          </div>
          <div className="dv-stat-pill">
            <span className="dv-stat-lbl">Avg</span>
            <strong>{stats.avg} {config.unit}</strong>
          </div>
          <div className="dv-stat-pill">
            <span className="dv-stat-lbl">Min</span>
            <strong>{stats.min}</strong>
          </div>
          <div className="dv-stat-pill">
            <span className="dv-stat-lbl">Max</span>
            <strong>{stats.max}</strong>
          </div>
          <div className="dv-stat-pill">
            <span className="dv-stat-lbl">Points</span>
            <strong>{data.length}</strong>
          </div>
        </div>
      )}

      {/* Activity stats */}
      {isActivity && (
        <div className="dv-stats-row">
          <div className="dv-stat-pill">
            <span className="dv-stat-lbl">Total steps</span>
            <strong style={{ color: config.color }}>
              {data.reduce((s, d) => s + (d.steps || 0), 0).toLocaleString()}
            </strong>
          </div>
          <div className="dv-stat-pill">
            <span className="dv-stat-lbl">Avg/day</span>
            <strong>
              {data.length
                ? Math.round(data.reduce((s, d) => s + (d.steps || 0), 0) / data.length).toLocaleString()
                : "—"}
            </strong>
          </div>
          <div className="dv-stat-pill">
            <span className="dv-stat-lbl">Days w/ goal</span>
            <strong style={{ color: "#10b981" }}>
              {data.filter(d => (d.steps || 0) >= 8000).length}/{data.length}
            </strong>
          </div>
        </div>
      )}

      {isEmpty ? (
        <div className="dv-vital-empty">
          No data for this {viewMode}. Try navigating to a different date.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          {isActivity ? (
            <BarChart data={data} margin={{ top: 8, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} interval={tickInterval} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
              <Tooltip content={<VitalTooltip unit="steps" />} />
              <ReferenceLine y={8000} stroke="#f59e0b" strokeDasharray="4 4"
                label={{ value: "Goal 8k", position: "insideTopRight", fontSize: 9, fill: "#f59e0b" }} />
              <Bar dataKey="steps" fill={config.color} name="Steps" radius={[3, 3, 0, 0]} fillOpacity={0.85} />
            </BarChart>
          ) : (
            <LineChart data={data} margin={{ top: 8, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} interval={tickInterval} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} domain={["auto", "auto"]} />
              <Tooltip content={<VitalTooltip unit={config.unit} />} />
              {config.refHigh > 0 && (
                <ReferenceLine y={config.refHigh} stroke="#ef444444" strokeDasharray="4 4"
                  label={{ value: `${config.refHigh}`, position: "insideTopRight", fontSize: 9, fill: "#ef4444" }} />
              )}
              {config.refLow > 0 && (
                <ReferenceLine y={config.refLow} stroke="#10b98144" strokeDasharray="4 4"
                  label={{ value: `${config.refLow}`, position: "insideBottomRight", fontSize: 9, fill: "#10b981" }} />
              )}
              <Line
                type="monotone"
                dataKey={config.dataKey}
                stroke={config.color}
                dot={viewMode === "day" ? { r: 2.5, fill: config.color } : false}
                strokeWidth={2}
                name={config.label}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      )}

      {/* Day view raw table */}
      {viewMode === "day" && !isEmpty && !isActivity && (
        <details className="dv-raw-table-wrap">
          <summary className="dv-raw-table-toggle">Show all {data.length} readings</summary>
          <div className="dv-raw-table-scroll">
            <table className="dv-raw-table">
              <thead>
                <tr><th>Time</th><th>{config.unit}</th></tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i}>
                    <td>{row.label}</td>
                    <td style={{ color: config.color, fontWeight: 600 }}>{row[config.dataKey]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}